import {
  StorageProviderAuthSchema,
  StorageProviderSchema,
} from '../definitions/schemas';
import { StorageProviders } from '../enums/Integrations/StorageProviders';

export enum ConnectionEvents {
  Connect = 'connect',
}

export enum ConnectionResult {
  Error = 'error',
  Success = 'success',
}

export type ConnectEventDetail = {
  auth: StorageProviderAuthSchema;
  message?: string;
  provider: StorageProviderSchema;
  result: ConnectionResult;
};

// Implement connection events
const eventManager = new EventTarget();

class StorageProviderService {
  public static authWindow: Window | null = null;

  public static provider: StorageProviderSchema | null = null;

  public static configure() {
    // file deepcode ignore InsufficientPostmessageValidation: multiple origins, we can't validate for just one
    window.addEventListener('message', (event: MessageEvent) => {
      if (
        StorageProviderService.authWindow &&
        StorageProviderService.provider &&
        !!event.data?.result
      ) {
        StorageProviderService.authWindow.close();
        StorageProviderService.authWindow = null;
        const { message, result, ...auth } = event.data;
        eventManager.dispatchEvent(
          new CustomEvent<ConnectEventDetail>(ConnectionEvents.Connect, {
            detail: {
              auth,
              message,
              provider: StorageProviderService.provider,
              result,
            },
          })
        );
        StorageProviderService.provider = null;
      }
    });
  }

  public static connect(provider: StorageProviderSchema) {
    /**
     * window.top defines the outermost frame which sometimes is not DSR,
     * so we return the current window.
     */
    const currentWindow = window.self;
    if (!currentWindow) return;
    if (StorageProviderService.authWindow?.closed) {
      StorageProviderService.authWindow = null;
      StorageProviderService.provider = null;
    }
    if (StorageProviderService.authWindow) {
      // Do NOT update StorageProviderService.provider
      StorageProviderService.authWindow.focus();
      return;
    }
    let height = 560; // theme.spacing(70);
    // Gong is an exception, its auth screen actually sucks
    if (provider.provider === StorageProviders.Gong) {
      height = 800;
    }
    const width = 480; // theme.spacing(60);
    const left =
      currentWindow.outerWidth / 2 + currentWindow.screenX - width / 2;
    const top =
      currentWindow.outerHeight / 2 + currentWindow.screenY - height / 2;
    const { link } = provider;
    if (!link) {
      return;
    }

    /**
     * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/open
     * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage
     */
    StorageProviderService.authWindow = window.open(
      link,
      `Ref_${btoa(link)}`,
      `height=${height},left=${left},top=${top},width=${width}`
    );
    StorageProviderService.provider = provider;
  }

  public static on(
    eventName: ConnectionEvents,
    callback: EventListenerOrEventListenerObject
  ) {
    eventManager.addEventListener(eventName, callback);
  }

  public static off(
    eventName: ConnectionEvents,
    callback: EventListenerOrEventListenerObject
  ) {
    eventManager.removeEventListener(eventName, callback);
  }
}

export default StorageProviderService;
